/**
 * DetailPageTemplate — Enhanced right sidebar drawer editor
 *
 * Used for quick-edit scenarios (e.g. Samples):
 * - MUI Drawer anchored right, ~55% width
 * - Gradient header with title + close button
 * - Tab bar (optional)
 * - Scrollable content area per tab
 * - Footer with Save / Delete actions
 * - Glassmorphism styling matching the rest of the app
 *
 * Props:
 *   open        — boolean, drawer visibility
 *   onClose     — callback to close(navigate away)
 *   title       — drawer header text
 *   headerColor — gradient key (primary, info, success, etc.)
 *   loading     — show skeleton while fetching
 *   tabs        — [{ label, content }]  (if omitted, renders children directly)
 *   children    — fallback content when no tabs
 *   onSave      — async save handler
 *   onDelete    — async delete handler (shows confirm)
 *   saveLabel   — button text
 *   deleteLabel — button text
 *   saveDisabled
 *   deleteDisabled
 *   width       — drawer width (default "55%")
 */

import { useState } from "react";
import PropTypes from "prop-types";
import {
  Drawer,
  Tabs,
  Tab,
  IconButton,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KDBox from "components/KDBox";
import KDTypography from "components/KDTypography";
import KDButton from "components/KDButton";
import { useMaterialUIController } from "context";
import { useT } from "context/I18nContext";

function DetailPageTemplate({
  open,
  onClose,
  title,
  headerColor,
  loading,
  tabs,
  children,
  onSave,
  onDelete,
  saveLabel,
  deleteLabel,
  saveDisabled,
  deleteDisabled,
  width,
}) {
  const t = useT();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [activeTab, setActiveTab] = useState(0);
  const [saving, setSaving] = useState(false);

  const handleTabChange = (_e, newValue) => {
    setActiveTab(newValue);
  };

  const handleSave = async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      await onSave();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    if (!window.confirm(t("editor.confirmDelete"))) return;
    await onDelete();
  };

  // Loading skeleton
  const renderSkeleton = () => (
    <KDBox px={2.5} py={2}>
      <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
      <Skeleton variant="rectangular" height={48} sx={{ mb: 2, borderRadius: 1 }} />
      <Skeleton variant="rectangular" height={48} sx={{ mb: 2, borderRadius: 1 }} />
      <Skeleton variant="rectangular" height={48} sx={{ mb: 2, borderRadius: 1 }} />
      <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
      <Skeleton variant="rectangular" height={48} sx={{ mb: 2, borderRadius: 1 }} />
    </KDBox>
  );

  // Tab content
  const renderContent = () => {
    if (loading) return renderSkeleton();

    if (tabs && tabs.length > 0) {
      return tabs.map((tab, idx) => (
        <KDBox
          key={idx}
          role="tabpanel"
          hidden={activeTab !== idx}
          sx={{
            flex: 1,
            overflow: "auto",
            display: activeTab === idx ? "flex" : "none",
            flexDirection: "column",
          }}
        >
          {activeTab === idx && (
            <KDBox px={2.5} py={2} flex={1}>
              {tab.content}
            </KDBox>
          )}
        </KDBox>
      ));
    }

    return (
      <KDBox px={2.5} py={2} flex={1} overflow="auto">
        {children}
      </KDBox>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: "80%", md: width },
          maxWidth: "900px",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          background: darkMode
            ? "linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)"
            : "#FAFAF9",
          borderLeft: darkMode
            ? "1px solid rgba(255,255,255,0.08)"
            : "1px solid rgba(0,0,0,0.05)",
        },
      }}
    >
      {/* ── Gradient Header ──────────────────── */}
      <KDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={2.5}
        py={2}
        sx={({ palette }) => {
          const grad = palette.gradients?.[headerColor] || palette.gradients?.info;
          const mainColor = palette[headerColor]?.main || palette.info.main;
          return {
            flexShrink: 0,
            background: grad
              ? `linear-gradient(135deg, ${grad.state} 0%, ${grad.main} 100%)`
              : `linear-gradient(135deg, ${mainColor}, ${mainColor})`,
            boxShadow: `0 4px 20px ${mainColor}30`,
          };
        }}
      >
        <KDTypography variant="h5" fontWeight="bold" color="white">
          {title}
        </KDTypography>
        <IconButton onClick={onClose} size="small" sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </KDBox>

      {/* ── Tabs (optional) ─────────────────── */}
      {tabs && tabs.length > 1 && (
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            flexShrink: 0,
            borderBottom: 1,
            borderColor: "divider",
            px: 1,
          }}
        >
          {tabs.map((tab, idx) => (
            <Tab key={idx} label={tab.label} />
          ))}
        </Tabs>
      )}

      {/* ── Content ─────────────────────────── */}
      <KDBox sx={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
        {renderContent()}
      </KDBox>

      {/* ── Footer ──────────────────────────── */}
      {(onSave || onDelete) && !loading && (
        <KDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={2.5}
          py={1.5}
          sx={{
            flexShrink: 0,
            borderTop: darkMode
              ? "1px solid rgba(255,255,255,0.08)"
              : "1px solid rgba(0,0,0,0.08)",
            backgroundColor: darkMode ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.95)",
          }}
        >
            <KDBox>
              {onDelete && (
                <KDButton
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={handleDelete}
                  disabled={deleteDisabled}
                >
                  {deleteLabel}
                </KDButton>
              )}
            </KDBox>
            <KDBox>
              {onSave && (
                <KDButton
                  variant="gradient"
                  color="info"
                  onClick={handleSave}
                  disabled={saveDisabled || saving}
                >
                  {saving ? (
                    <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                  ) : null}
                  {saveLabel}
                </KDButton>
              )}
            </KDBox>
          </KDBox>
      )}
    </Drawer>
  );
}

DetailPageTemplate.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  headerColor: PropTypes.string,
  loading: PropTypes.bool,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      content: PropTypes.node.isRequired,
    })
  ),
  children: PropTypes.node,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
  saveLabel: PropTypes.string,
  deleteLabel: PropTypes.string,
  saveDisabled: PropTypes.bool,
  deleteDisabled: PropTypes.bool,
  width: PropTypes.string,
};

DetailPageTemplate.defaultProps = {
  title: "",
  headerColor: "info",
  loading: false,
  tabs: null,
  children: null,
  onSave: null,
  onDelete: null,
  saveLabel: "Save",
  deleteLabel: "Delete",
  saveDisabled: false,
  deleteDisabled: false,
  width: "55%",
};

export default DetailPageTemplate;
